/**
 * RunDetailPage — the canonical, full-bleed investigation surface for a workflow
 * run. Keyed by run id (`/runs/$runId`); the suite test detail, the flow-designer
 * run panel, and the issue drawer all link here.
 *
 * Layout mirrors the flow designer's interactive spaces: a slim bordered header
 * (icon + title + status + icon buttons), then an edge-to-edge three-column
 * "lockup" — navigable browser actions on the left, the video as the hero in the
 * middle, and the tabbed Network / Thoughts / Console inspector on the right —
 * all synced to playback. Root cause analysis and run extras sit below.
 *
 * Optional `?testRunId=` layers in suite/test context (attempt history, rerun,
 * linked issue, scenario label). `?workflowId=` lets internal callers skip the
 * run→workflow lookup round-trip.
 *
 * @module ui/pages/RunDetailPage/RunDetailPage
 */

import React from 'react';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import {
  Page,
  PageHeader,
  Text,
  Button,
  Flex,
  TagBadge,
  LoadingOrbit,
  VideoPlayerWithTimeline,
  Drawer,
  DrawerTrigger,
  useToast,
} from '@chatsdet/ui';
import { FlowArrow } from '@phosphor-icons/react';
import { mapFastToFull, mapFullToFast } from '@chatsdet/types';
import type { FastVideoTimeline, VideoTimeline, WorkflowAssertionSummary } from '@chatsdet/types';
import { rootCauseSeverityHex } from '../../components/issues/diagnosticDisplay';
import { OrgSwitchPrompt } from '../../components/OrgSwitchPrompt';
import { API_URL } from '../../../config';
import { ApiError } from '../../../lib/api-error';
import { useBreadcrumbs } from '../../../contexts/BreadcrumbsContext';
import { useRunLookup } from '../../../hooks/useRunLookup';
import { useWorkflowDefinition } from '../../../hooks/useWorkflowNodes';
import { useRunSnapshotNodes } from '../FlowDesigner/runs/useRunSnapshotNodes';
import { hasUnavailableRecoveredStepHistory } from '../FlowDesigner/runs/RunStepList';
import { useWorkflowRun } from '../FlowDesigner/hooks/useWorkflowRun';
import type { SerializedTrackedVariableDto } from '../FlowDesigner/hooks/useWorkflowRun';
import { useWorkflowRuns } from '../FlowDesigner/hooks/useWorkflowRuns';
import {
  useWorkflowRunThoughts,
  calculateThoughtTimestamps,
} from '../FlowDesigner/hooks/useWorkflowRunThoughts';
import { useWorkflowRunDiagnostics } from '../../../hooks/useWorkflowRunDiagnostics';
import { useRunFailureDiagnostic } from '../../../hooks/useRunFailureDiagnostic';
import { useWorkflowOpenIssues } from '../../../hooks/useWorkflowOpenIssues';
import { useAssertionIssueLinkContext } from '../../../hooks/useAssertionIssueLinkContext';
import { useTestRunDetail } from '../../../features/workflows/useTestRunDetail';
import { useRerunAgenticWorkflow } from '../../../features/workflows/useAgenticRunDetail';
import { RerunTestButton } from '../../../features/workflows/RerunTestButton';
import { NoVideoEmptyState } from '../../../features/workflows/NoVideoEmptyState';
import { FailureDiagnosticSection } from '../FlowDesigner/runs/FailureDiagnosticSection';
import { RunDebugDrawer } from '../FlowDesigner/runs/debug/RunDebugDrawer';
import { RunHistoryBar } from '../../components/RunHistoryBar';
import { FlakyAttemptCard } from '../../components/FlakyAttemptCard';
import { LinkedIssueCard } from '../../components/LinkedIssueCard';
import { IssueDrawerV2 } from '../../components/issues/IssueDrawerV2';
import { RunTimelineInspector } from './RunTimelineInspector';
import { RunActivityPanel, type ActivityTab } from './RunActivityPanel';
import { RunDetailHeader } from './RunDetailHeader';
import { RunHealthStrip } from './RunHealthStrip';
import type { StepCacheMetadata } from '@chatsdet/ui';

// Both maps fall back to the raw wire status (see the `??` at the header's
// label call site), so a status missing here renders as the literal database
// value — which is exactly how "inconclusive" was leaking into the UI.
const testRunLabelMap: Record<string, string> = {
  queued: 'Queued',
  running: 'Running',
  waiting: 'Waiting',
  success: 'Passed',
  failed: 'Failed',
  inconclusive: 'Platform error',
  skipped: 'Skipped',
  degraded: 'Degraded',
  flaky_success: 'Flaky',
  canceled: 'Canceled',
  timeout: 'Timed Out',
};

const runStatusLabelMap: Record<string, string> = {
  queued: 'Queued',
  running: 'Running',
  success: 'Passed',
  failed: 'Failed',
  inconclusive: 'Platform error',
  canceled: 'Canceled',
  timeout: 'Timed Out',
};

export const RunDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { runId } = useParams({ from: '/runs/$runId' });
  const search = useSearch({ strict: false }) as {
    testRunId?: string;
    workflowId?: string;
    debug?: boolean;
  };
  const { toast } = useToast();

  const [currentTimeMs, setCurrentTimeMs] = React.useState(0);
  const [seekVersion, setSeekVersion] = React.useState(0);
  const [loadDiagnostics, setLoadDiagnostics] = React.useState(false);
  const [isIssueDrawerOpen, setIsIssueDrawerOpen] = React.useState(false);
  const [activityTab, setActivityTab] = React.useState<ActivityTab>('summary');
  // Which marker set the video scrubber shows: the events timeline, or the
  // diagnostic root-cause timeline (only available once a diagnostic completes).
  const [timelineMode, setTimelineMode] = React.useState<'events' | 'diagnostic'>('events');
  // Which video variant plays: the full recording, or the condensed "fast"
  // cut (dead frames removed). Only offered when the run has a fast variant.
  const [videoVariant, setVideoVariant] = React.useState<'full' | 'fast'>('full');

  // Resolve workflowId (skips the request when the caller passed ?workflowId=).
  const lookup = useRunLookup(runId, search.workflowId);
  const workflowId = lookup.workflowId;

  const runQuery = useWorkflowRun(workflowId, runId);
  const run = runQuery.data?.data?.run;

  const {
    name: workflowName,
    nodes: liveNodes,
    health: workflowHealth,
    propertyId: workflowPropertyId,
  } = useWorkflowDefinition(workflowId);
  // Prefer the run-pinned snapshot's nodes (execution-time truth) over the
  // live definition, which may have been edited since this run.
  const { nodes, fromSnapshot: nodesFromSnapshot } = useRunSnapshotNodes(
    workflowId,
    run,
    liveNodes
  );

  // Last 10 runs for this workflow — powers the header health strip (each
  // segment loads that run; the current one is marked active).
  const recentRunsQuery = useWorkflowRuns(workflowId, { page: 1, pageSize: 10 });
  const recentRuns = recentRunsQuery.data?.data.runs ?? [];
  const { data: thoughts = [] } = useWorkflowRunThoughts(workflowId, runId);
  const diagnosticsQuery = useWorkflowRunDiagnostics(
    workflowId ?? undefined,
    runId,
    loadDiagnostics
  );
  const failureDiagnostic = useRunFailureDiagnostic(
    workflowId ?? undefined,
    runId,
    run?.status === 'failed'
  );
  // Open issues persistently linked to this flow (not just this run) — powers
  // the "Has issues (N)" affordance and, when there's more than one, expands
  // FailureDiagnosticSection's single "View Issue" button into a list.
  const openIssuesQuery = useWorkflowOpenIssues(workflowId);
  const openIssues = openIssuesQuery.data ?? [];
  // Filing a new issue from a failing assertion requires a primary URL —
  // see `useAssertionIssueLinkContext`.
  const issueLinkContext = useAssertionIssueLinkContext(run, workflowId, workflowPropertyId);

  // Optional suite/test context.
  const testRunQuery = useTestRunDetail(search.testRunId);
  const testRun = testRunQuery.data?.testRun;
  const attempts = testRunQuery.data?.attempts ?? [];

  const timeline = (run?.videoTimelineJson as VideoTimeline) ?? null;
  const thoughtsWithTimestamps = React.useMemo(
    () => calculateThoughtTimestamps(thoughts, timeline),
    [thoughts, timeline]
  );

  // Fast-variant plumbing. Page state (currentTimeMs, activity panel, inspector,
  // diagnostics) always lives in the FULL video's timebase; positions translate
  // through the variant's cut map at the player boundary.
  const fastTimeline = (run?.fastVideoTimelineJson as FastVideoTimeline | null) ?? null;
  const hasFastVariant = Boolean(run?.fastVideoStorageKey && fastTimeline);
  const activeVariant: 'full' | 'fast' =
    hasFastVariant && videoVariant === 'fast' ? 'fast' : 'full';

  // Display copy of the fast timeline with the legacy injected "Skipped Ns idle"
  // markers stripped (older runs persisted them into fastVideoTimelineJson; the
  // condenser no longer emits them). Keep the RAW `fastTimeline` for all cut-map
  // math below — only `entries` differ, the cut/duration fields are intact.
  const fastTimelineForDisplay = React.useMemo<FastVideoTimeline | null>(
    () =>
      fastTimeline
        ? {
            ...fastTimeline,
            entries: fastTimeline.entries.filter(
              (entry) => !(entry.isAnnotation && entry.target === 'time-skip')
            ),
          }
        : null,
    [fastTimeline]
  );

  // The timeline the scrubber + left activity panel render: the skip-filtered
  // fast variant when fast is active, else the full timeline.
  const displayTimeline = activeVariant === 'fast' ? fastTimelineForDisplay : timeline;

  const handleTimeUpdate = React.useCallback(
    (timeMs: number) => {
      setCurrentTimeMs(
        activeVariant === 'fast' && fastTimeline
          ? mapFastToFull(timeMs, fastTimeline.cutIntervals, fastTimeline.fullDurationMs)
          : timeMs
      );
    },
    [activeVariant, fastTimeline]
  );

  const handleVariantChange = React.useCallback((variant: 'full' | 'fast') => {
    setVideoVariant(variant);
    // Bump the seek request so the player resumes at the equivalent position
    // in the new variant's timebase once the swapped source loads.
    setSeekVersion((v) => v + 1);
  }, []);

  // Scroll target for "Jump to root cause" — the RCA section lives in the
  // right column's scrolling container.
  const rcaRef = React.useRef<HTMLDivElement | null>(null);
  const handleJumpToRootCause = React.useCallback(() => {
    rcaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // Cache/agent badges for the browser-action step groups + video scrubber.
  const stepMetadata = React.useMemo(() => {
    if (!run) return undefined;
    const map: Record<string, StepCacheMetadata> = {};
    for (const step of run.steps) {
      if (step.metadataJson?.cacheOutcome) {
        map[step.nodeId] = {
          cacheOutcome: step.metadataJson.cacheOutcome as string | undefined,
          cacheHitSource: step.metadataJson.cacheHitSource as string | undefined,
          cacheOutcomeReason: step.metadataJson.cacheOutcomeReason as string | undefined,
          waitShortcut: step.metadataJson.waitShortcut as boolean | undefined,
        };
      }
    }
    return Object.keys(map).length > 0 ? map : undefined;
  }, [run]);

  // Bumped on every direct user action (play / scrub / clicking a step) so the
  // timeline reclaims auto-follow after the user has scrolled it away. The
  // user's direct action always wins over automated scrolling.
  const [resyncNonce, setResyncNonce] = React.useState(0);
  const bumpResync = React.useCallback(() => setResyncNonce((n) => n + 1), []);

  const handleSeek = React.useCallback(
    (timestampMs: number) => {
      setCurrentTimeMs(timestampMs);
      setSeekVersion((v) => v + 1);
      bumpResync();
    },
    [bumpResync]
  );

  // Seek requests originate in full-video time; translate for the fast variant.
  const seekRequest = React.useMemo(
    () =>
      seekVersion > 0
        ? {
            timeMs:
              activeVariant === 'fast' && fastTimeline
                ? mapFullToFast(
                    currentTimeMs,
                    fastTimeline.cutIntervals,
                    fastTimeline.fastDurationMs
                  )
                : currentTimeMs,
            version: seekVersion,
          }
        : null,
    [currentTimeMs, seekVersion, activeVariant, fastTimeline]
  );

  // The left activity timeline renders `displayTimeline` entries (fast-timebase
  // when fast), so its playhead must be in the same timebase as those entries.
  const activityCurrentTimeMs = React.useMemo(
    () =>
      activeVariant === 'fast' && fastTimeline
        ? mapFullToFast(currentTimeMs, fastTimeline.cutIntervals, fastTimeline.fastDurationMs)
        : currentTimeMs,
    [currentTimeMs, activeVariant, fastTimeline]
  );

  // Clicks in the activity timeline arrive in the displayed timebase; map fast
  // back to full before it hits page state (which is always full-video time).
  const handleActivitySeek = React.useCallback(
    (timestampMs: number) =>
      handleSeek(
        activeVariant === 'fast' && fastTimeline
          ? mapFastToFull(timestampMs, fastTimeline.cutIntervals, fastTimeline.fullDurationMs)
          : timestampMs
      ),
    [handleSeek, activeVariant, fastTimeline]
  );

  // Relative "M:SS into the video" labels in the diagnostics panels should read
  // in the fast timebase when fast plays, matching the scrubber. Identity in
  // full mode. (Absolute wall-clock + sync stay in full time — see panels.)
  const displayTimeMs = React.useMemo(
    () =>
      activeVariant === 'fast' && fastTimeline
        ? (fullMs: number) =>
            mapFullToFast(fullMs, fastTimeline.cutIntervals, fastTimeline.fastDurationMs)
        : undefined,
    [activeVariant, fastTimeline]
  );

  // The diagnostic's root-cause timeline, projected onto the video scrubber as
  // an alternate marker set. Each event becomes an annotation marker colored by
  // severity; the player's switcher toggles the scrubber to this set.
  const diagnosticTimeline = React.useMemo<VideoTimeline | null>(() => {
    const events = failureDiagnostic.diagnostic?.rootCauseTimeline;
    if (!events || events.length === 0) return null;
    const baseIso = timeline?.startTimestamp ?? run?.startedAt ?? new Date(0).toISOString();
    const baseMs = Date.parse(baseIso);
    return {
      startTimestamp: baseIso,
      entries: events.map((event) => {
        const timestampMs = Math.max(0, event.timestampMs);
        return {
          action: 'annotation',
          target: event.title,
          success: true,
          timestampMs,
          isoTimestamp: new Date((Number.isNaN(baseMs) ? 0 : baseMs) + timestampMs).toISOString(),
          isAnnotation: true,
          annotationType: 'diagnostic-event',
          annotationColor: rootCauseSeverityHex(event.severity),
        };
      }),
    };
  }, [failureDiagnostic.diagnostic, timeline?.startTimestamp, run?.startedAt]);

  // The diagnostic markers are built in full-video time; project them onto the
  // fast variant's timebase when that's what the scrubber is showing.
  const playerDiagnosticTimeline = React.useMemo<VideoTimeline | null>(() => {
    if (!diagnosticTimeline || activeVariant !== 'fast' || !fastTimeline) {
      return diagnosticTimeline;
    }
    return {
      ...diagnosticTimeline,
      entries: diagnosticTimeline.entries.map((entry) => ({
        ...entry,
        timestampMs: mapFullToFast(
          entry.timestampMs,
          fastTimeline.cutIntervals,
          fastTimeline.fastDurationMs
        ),
      })),
    };
  }, [diagnosticTimeline, activeVariant, fastTimeline]);

  // Clicking a key-evidence row seeks the video AND flips the scrubber to the
  // diagnostic timeline so the event is visibly marked.
  const handleSelectDiagnosticEvent = React.useCallback(
    (timestampMs: number) => {
      handleSeek(timestampMs);
      setTimelineMode('diagnostic');
    },
    [handleSeek]
  );

  const rerunMutation = useRerunAgenticWorkflow();

  // Title + status: prefer the richer test-run status when in suite context.
  const version = run?.snapshotVersion ?? 1;
  const scenarioLabel = testRun?.scenarioName?.trim();
  const baseTitle = workflowName ?? testRunQuery.data?.workflow?.name ?? 'Workflow Run';
  const pageTitle = scenarioLabel
    ? `${baseTitle} → ${scenarioLabel} (v${version})`
    : `${baseTitle} (v${version})`;

  useBreadcrumbs([
    { label: 'Runs', onPress: () => navigate({ to: '/runs' }) },
    { label: pageTitle },
  ]);

  if (lookup.isLoading || runQuery.isLoading) {
    return (
      <Page>
        <div className="flex items-center justify-center py-12">
          <LoadingOrbit size="lg" />
        </div>
      </Page>
    );
  }

  const wrongOrgInfo = lookup.wrongOrgInfo ?? runQuery.wrongOrgInfo;
  if (wrongOrgInfo) {
    return (
      <Page>
        <OrgSwitchPrompt
          resourceType="run"
          targetOrgId={wrongOrgInfo.orgId}
          targetOrgName={wrongOrgInfo.orgName}
          redirectUrl={`/runs/${runId}`}
          onCancel={() => {
            lookup.clearWrongOrgInfo();
            runQuery.clearWrongOrgInfo();
            navigate({ to: '/runs' });
          }}
        />
      </Page>
    );
  }

  if (lookup.isError || runQuery.isError || !run || !workflowId) {
    return (
      <Page>
        <PageHeader title="Run Not Found" description="The requested run could not be found" />
        <div className="text-center py-12 border-2 border-dashed border-border rounded-lg">
          <Text className="text-muted-foreground">
            This run may have been deleted or you may not have access.
          </Text>
          <Button
            variant="outline"
            size="sm"
            onPress={() => navigate({ to: '/runs' })}
            className="mt-4"
          >
            Back to Runs
          </Button>
        </div>
      </Page>
    );
  }

  const isRunning = run.status === 'queued' || run.status === 'running';
  const statusKey = testRun?.status ?? run.status;
  const statusLabel = testRun
    ? (testRunLabelMap[testRun.status] ?? testRun.status)
    : (runStatusLabelMap[run.status] ?? run.status);

  const activeVideoStorageKey =
    activeVariant === 'fast' ? run.fastVideoStorageKey : run.videoStorageKey;
  // `<video>`/`<img>` send the session cookie, so they resolve in the active org.
  const videoSrc = activeVideoStorageKey
    ? `${API_URL}/videos/${encodeURIComponent(activeVideoStorageKey)}`
    : null;
  const posterSrc = run.steps?.[0]?.screenshotS3Key
    ? `${API_URL}/screenshots/${encodeURIComponent(run.steps[0].screenshotS3Key)}`
    : null;

  // The Debug launcher is intentionally not rendered — the drawer stays
  // mounted below and still opens from `?debug=true&debug_tab=overview`, but
  // nothing in the UI points at it. Same in the Flow Designer's
  // `RunDetailsPanel`. Remove `RunDebugDrawer` and the router's
  // `debug`/`debug_tab` search params if it's confirmed dead.

  // Shared blocks — the chat layout pins the rail across the whole scroll, so it
  // keeps the right-side content (incl. RCA) in one scrolling column; other tabs
  // keep the RCA full-width below the lockup.
  const activityPanel = (
    <RunActivityPanel
      steps={run.steps}
      nodes={nodes}
      nodesFromSnapshot={nodesFromSnapshot}
      timeline={displayTimeline}
      currentTimeMs={activityCurrentTimeMs}
      onSeek={handleActivitySeek}
      stepMetadata={stepMetadata}
      resyncSignal={resyncNonce}
      selectedTab={activityTab}
      onTabChange={setActivityTab}
      workflowId={workflowId}
      runId={run.id}
      issueLinkContext={issueLinkContext}
      runStatus={run.status}
      runErrorMessage={run.errorMessage}
      stepHistoryUnavailable={hasUnavailableRecoveredStepHistory(run.steps, run.metadataJson)}
      assertionSummary={
        (run.metadataJson?.assertionSummary as WorkflowAssertionSummary | undefined) ?? null
      }
      trackedVariables={
        (run.metadataJson?.trackedVariables as SerializedTrackedVariableDto[] | undefined) ??
        undefined
      }
      diagnosticStatus={failureDiagnostic.status}
      diagnostic={failureDiagnostic.diagnostic}
      onDiagnose={failureDiagnostic.triggerDiagnostic}
      isDiagnosing={failureDiagnostic.isTriggering}
      onJumpToRootCause={handleJumpToRootCause}
      onSelectDiagnosticEvent={handleSelectDiagnosticEvent}
      className="h-full"
    />
  );

  const videoBlock = (
    <div className="flex shrink-0 flex-col bg-muted/30">
      {videoSrc || posterSrc ? (
        <VideoPlayerWithTimeline
          posterSrc={posterSrc}
          videoSrc={videoSrc}
          timeline={displayTimeline}
          alt="Run recording"
          seekRequest={seekRequest}
          onTimeUpdate={handleTimeUpdate}
          onPlayStart={bumpResync}
          onScrub={bumpResync}
          stepMetadata={stepMetadata}
          diagnosticTimeline={playerDiagnosticTimeline}
          timelineMode={timelineMode}
          onTimelineModeChange={setTimelineMode}
          hasFastVariant={hasFastVariant}
          videoVariant={activeVariant}
          onVideoVariantChange={handleVariantChange}
          flush
          showFullscreen={false}
          className="w-full"
        />
      ) : (
        <div className="m-auto w-full max-w-md p-4">
          <NoVideoEmptyState reason={null} isRunning={isRunning} />
        </div>
      )}
    </div>
  );

  const inspectorBlock = (
    <RunTimelineInspector
      thoughts={thoughtsWithTimestamps}
      consoleLogs={diagnosticsQuery.data?.consoleLogs}
      networkRequests={diagnosticsQuery.data?.networkRequests}
      videoStartedAt={diagnosticsQuery.data?.videoStartedAt}
      diagnosticsLoading={diagnosticsQuery.isLoading && loadDiagnostics}
      onLoadDiagnostics={() => setLoadDiagnostics(true)}
      currentTimeMs={currentTimeMs}
      onSeek={handleSeek}
      displayTimeMs={displayTimeMs}
      className="h-[280px] shrink-0 border-t border-border"
    />
  );

  const rcaBlock = (
    <>
      {openIssues.length > 0 && (
        <Flex align="center" gap="xs" wrap>
          <TagBadge name={`Has issues (${openIssues.length})`} color="red" hideDot size="sm" />
          <Flex gap="sm" wrap className="flex-1 min-w-0">
            {openIssues.map((issue) => (
              <a
                key={issue.id}
                href={`/issues?issueId=${issue.id}`}
                className="text-sm text-primary hover:underline truncate"
              >
                <span className="font-mono text-xs text-muted-foreground mr-1">
                  {issue.shortcode}
                </span>
                {issue.title}
              </a>
            ))}
          </Flex>
        </Flex>
      )}
      {run.status === 'failed' && (
        // Stable wrapper so "Jump to root cause" has a scroll target that
        // doesn't remount as the diagnostic moves through its states.
        <div ref={rcaRef} className="scroll-mt-4">
          <FailureDiagnosticSection
            status={failureDiagnostic.status}
            diagnostic={failureDiagnostic.diagnostic}
            onTrigger={failureDiagnostic.triggerDiagnostic}
            isTriggering={failureDiagnostic.isTriggering}
            linkedIssueId={testRun?.linkedIssueId}
            onViewIssue={() => setIsIssueDrawerOpen(true)}
            openIssues={openIssues}
          />
        </div>
      )}
      {attempts.length > 0 && search.testRunId && (
        <RunHistoryBar runs={attempts} currentRunId={search.testRunId} />
      )}
      {testRun?.status === 'flaky_success' && testRun.metadataJson?.failedAttempt && (
        <FlakyAttemptCard failedAttempt={testRun.metadataJson.failedAttempt} />
      )}
      {testRun?.status === 'skipped' && testRun.skipReason && (
        <div className="rounded-lg border border-border bg-muted/30 px-4 py-3">
          <Text size="sm" className="text-muted-foreground">
            Skipped: {testRun.skipReason}
          </Text>
        </div>
      )}
      {testRun?.linkedIssueId && (
        <LinkedIssueCard
          issueId={testRun.linkedIssueId}
          onOpenDrawer={() => setIsIssueDrawerOpen(true)}
        />
      )}
    </>
  );

  return (
    <>
      {/* Bound the page to the viewport (below the 52px app top bar) so the run
          header stays fixed and the body owns the scroll — this lets the chat
          rail fill the viewport with its composer pinned at the bottom. */}
      <div className="flex h-[calc(100dvh-52px)] flex-col overflow-hidden">
        <RunDetailHeader
          title={pageTitle}
          status={statusKey}
          statusLabel={statusLabel}
          startedAt={run.startedAt}
          finishedAt={run.finishedAt}
          isRunning={run.status === 'running'}
          environment={run.environment}
          environmentInstanceUrl={run.environmentInstance?.publicUrl}
          sandbox={run.execution?.sandbox}
          actions={
            <>
              {workflowId && recentRuns.length > 0 && (
                <>
                  <RunHealthStrip runs={recentRuns} activeRunId={runId} health={workflowHealth} />
                  <span className="h-5 w-px bg-border" aria-hidden="true" />
                </>
              )}
              {search.testRunId && testRun && (
                <RerunTestButton
                  iconOnly
                  availability={testRun.rerunAvailability}
                  isPending={rerunMutation.isPending}
                  onPress={async () => {
                    try {
                      await rerunMutation.mutateAsync({
                        testRunId: search.testRunId!,
                        executionId: testRun?.executionId ?? '',
                      });
                      toast.success('Rerun started');
                    } catch (err) {
                      toast.error(err instanceof Error ? err.message : 'Failed to rerun', {
                        reportError: !(err instanceof ApiError && err.userError),
                      });
                    }
                  }}
                />
              )}
              <Button
                variant="icon"
                size="sm"
                iconOnly
                tooltip="View flow"
                aria-label="View flow"
                onPress={() => navigate({ to: '/flows/$workflowId', params: { workflowId } })}
              >
                <FlowArrow size={16} />
              </Button>
            </>
          }
        />

        {/* Body — one two-column layout for every tab: the activity rail
            (Summary / Timeline / Chat) fills the bounded height on the left; the
            result (video hero, inspector, RCA) scrolls as one column on the
            right. The rail widens for the chat investigator. */}
        <div className="flex min-h-0 flex-1">
          <div
            className={`shrink-0 border-r border-border transition-[width] duration-200 ${
              activityTab === 'chat' ? 'w-[480px]' : 'w-[340px]'
            }`}
          >
            {activityPanel}
          </div>
          <div className="flex min-w-0 flex-1 flex-col overflow-y-auto">
            {videoBlock}
            {inspectorBlock}
            <div className="space-y-5 border-t border-border bg-card px-4 py-5">{rcaBlock}</div>
          </div>
        </div>
      </div>

      {/* Consolidated debug drawer — driven by ?debug=1 */}
      <RunDebugDrawer run={run} nodes={nodes} />

      {/* Issue drawer */}
      {testRun?.linkedIssueId && (
        <DrawerTrigger isOpen={isIssueDrawerOpen} onOpenChange={setIsIssueDrawerOpen}>
          <span style={{ display: 'none' }} />
          <Drawer hideCloseButton>
            <IssueDrawerV2
              issueId={testRun.linkedIssueId}
              pageIndex={0}
              totalItems={1}
              canNavigate={false}
              onNavigatePrev={() => {}}
              onNavigateNext={() => {}}
              onClose={() => setIsIssueDrawerOpen(false)}
            />
          </Drawer>
        </DrawerTrigger>
      )}
    </>
  );
};
